export type CanvasViewport = { x: number; y: number; zoom: number };
export type Point = { x: number; y: number };

export const TOUCH_DRAG_THRESHOLD_PX = 8;

/** Keep a tap selectable until the finger has moved far enough to mean pan. */
export function movedPastTouchDragThreshold(
  start: Point,
  current: Point,
  threshold = TOUCH_DRAG_THRESHOLD_PX,
): boolean {
  return Math.hypot(current.x - start.x, current.y - start.y) >= threshold;
}

/** Translate the viewport by the same screen-space delta as one finger. */
export function viewportForTouchPan(
  startViewport: CanvasViewport,
  start: Point,
  current: Point,
): CanvasViewport {
  return {
    x: startViewport.x + current.x - start.x,
    y: startViewport.y + current.y - start.y,
    zoom: startViewport.zoom,
  };
}

/** Native listener session used by cable hit strips, whose React Flow wrapper
 * is deliberately `nopan`. It owns exactly one pointer and yields as soon as a
 * second touch appears so the canvas pinch arbiter can take over. */
export function bindCableTouchGesture({
  eventTarget,
  pointerId,
  start,
  startViewport,
  onPan,
  onTap,
  onCancel,
  onFinish,
}: {
  eventTarget: EventTarget;
  pointerId: number;
  start: Point;
  startViewport: CanvasViewport;
  onPan: (viewport: CanvasViewport) => void;
  onTap: (event: PointerEvent) => void;
  onCancel: () => void;
  onFinish?: () => void;
}): () => void {
  let active = true;
  let panning = false;

  const cleanup = () => {
    if (!active) return false;
    active = false;
    eventTarget.removeEventListener("pointermove", onMove);
    eventTarget.removeEventListener("pointerup", onUp);
    eventTarget.removeEventListener("pointercancel", onPointerCancel);
    eventTarget.removeEventListener("touchstart", onAdditionalTouch, true);
    return true;
  };
  const onMove = (event: Event) => {
    const pointerEvent = event as PointerEvent;
    if (pointerEvent.pointerId !== pointerId) return;
    const current = { x: pointerEvent.clientX, y: pointerEvent.clientY };
    if (!panning && !movedPastTouchDragThreshold(start, current)) return;
    panning = true;
    pointerEvent.preventDefault();
    onPan(viewportForTouchPan(startViewport, start, current));
  };
  const onUp = (event: Event) => {
    const pointerEvent = event as PointerEvent;
    if (pointerEvent.pointerId !== pointerId) return;
    if (!cleanup()) return;
    onFinish?.();
    if (!panning) onTap(pointerEvent);
  };
  const onPointerCancel = (event: Event) => {
    const pointerEvent = event as PointerEvent;
    if (pointerEvent.pointerId !== pointerId) return;
    if (!cleanup()) return;
    onFinish?.();
    onCancel();
  };
  const onAdditionalTouch = (event: Event) => {
    if ((event as TouchEvent).touches.length < 2) return;
    if (!cleanup()) return;
    onFinish?.();
    onCancel();
  };

  eventTarget.addEventListener("pointermove", onMove, { passive: false });
  eventTarget.addEventListener("pointerup", onUp);
  eventTarget.addEventListener("pointercancel", onPointerCancel);
  eventTarget.addEventListener("touchstart", onAdditionalTouch, {
    capture: true,
    passive: true,
  });
  return () => {
    cleanup();
  };
}

/** Keep one flow-space point under the moving two-finger centroid. */
export function viewportForPinch({
  flowAnchor,
  centroid,
  rendererOrigin,
  zoom,
}: {
  flowAnchor: Point;
  centroid: Point;
  rendererOrigin: Point;
  zoom: number;
}): CanvasViewport {
  return {
    x: centroid.x - rendererOrigin.x - flowAnchor.x * zoom,
    y: centroid.y - rendererOrigin.y - flowAnchor.y * zoom,
    zoom,
  };
}

export function touchCentroid(touches: readonly Point[]): Point {
  const first = touches[0];
  const second = touches[1];
  return {
    x: (first.x + second.x) / 2,
    y: (first.y + second.y) / 2,
  };
}

export function touchDistance(touches: readonly Point[]): number {
  const first = touches[0];
  const second = touches[1];
  return Math.hypot(
    second.x - first.x,
    second.y - first.y,
  );
}
